import React from "react";

interface TableColumn {
  Header: string;
  accessor: string;
  Cell?: (props: { value: any, row: { original: any } }) => React.ReactNode;
}

interface TableData {
  [key: string]: any; // Allow any data type for flexibility
}

interface CustomTableProps {
  columns: TableColumn[];
  data: TableData[];
  actions?: (row: TableData) => React.ReactNode;
  status?: (row: TableData) => React.ReactNode;
  title?: string;
  statusName?: string;
}

const getNestedValue = (obj: TableData, accessor: string) => {
  return accessor.split(".").reduce((acc, key) => acc?.[key], obj);
};

const isValidUrl = (value: string) => {
  try {
    new URL(value);
    return true;
  } catch (_) {
    return false;
  }
};

const isImageUrl = (url: string) => {
  return /\.(jpeg|jpg|png|svg)$/.test(url);
};

const CustomTable: React.FC<CustomTableProps> = ({
  columns,
  data,
  actions,
  status,
  title,
  statusName = "Détails",
}) => {
  return (
    <div className="w-full bg-white/70 backdrop-blur-xl rounded-[2.5rem] border border-white shadow-2xl shadow-black/5 overflow-hidden">
      {title && (
        <div className="px-8 py-6 border-b border-gray-50 flex items-center justify-between">
          <h3 className="text-[10px] font-black text-title uppercase tracking-[0.3em]">{title}</h3>
          <div className="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
        </div>
      )}
      <div className="overflow-x-auto no-scrollbar max-h-[70vh]">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-50/50">
              {columns.map((column) => (
                <th
                  key={column.accessor}
                  className="sticky top-0 z-20 bg-white/90 backdrop-blur-md px-6 py-5 text-[10px] font-black text-text/40 uppercase tracking-widest border-b border-gray-100"
                >
                  {column.Header}
                </th>
              ))}
              {status && (
                <th className="sticky top-0 z-20 bg-white/90 backdrop-blur-md px-6 py-5 text-[10px] font-black text-text/40 uppercase tracking-widest border-b border-gray-100 text-center">
                  {statusName}
                </th>
              )}
              {actions && (
                <th className="sticky top-0 z-20 bg-white/90 backdrop-blur-md px-6 py-5 text-[10px] font-black text-text/40 uppercase tracking-widest border-b border-gray-100 text-center min-w-[300px]">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {data.map((row, rowIndex) => (
              <tr key={rowIndex} className="group hover:bg-primary/[0.02] transition-colors">
                {columns.map((column) => {
                  const value: any = getNestedValue(row, column.accessor);
                  return (
                    <td
                      key={column.accessor}
                      className="px-6 py-5"
                    >
                      {(() => {
                        if (column.Cell) {
                          return column.Cell({ value, row: { original: row } });
                        }
                        
                        if (["startDate", "endDate", "subscriptionStartDate", "subscriptionEndDate", "createdAt", "updatedAt"].includes(column.accessor)) {
                          if (!value) return <span className="text-text/20">—</span>;
                          const dateStr = typeof value === "string" ? value.slice(0, 10) : (value instanceof Date ? value.toISOString().slice(0, 10) : String(value));
                          return <span className="text-[12px] font-bold text-title">{dateStr}</span>;
                        } 
                        
                        if (column.accessor === "isSubscriptionActive") {
                          const isActive = !!value;
                          return (
                            <div className="flex justify-center">
                              <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.1em] border transition-all shadow-md ${isActive ? 'bg-[#10B981] text-white border-[#059669]' : 'bg-[#EF4444] text-white border-[#DC2626]'}`}>
                                <span className={`w-2 h-2 rounded-full bg-white ${isActive ? 'animate-pulse' : ''}`}></span>
                                {isActive ? "Actif" : "Expiré"}
                              </span>
                            </div>
                          );
                        } 
                        
                        if (Array.isArray(value)) {
                          const formatted = value
                            .map((item) => {
                              if (typeof item === "string") return item;
                              if (item && typeof item === "object") return item.title || item.speciality || item.name || item.subject || item.id;
                              return item;
                            })
                            .filter(Boolean)
                            .join(", ");
                          return (
                            <div className="max-w-[200px] truncate">
                              <span className="text-[11px] font-medium text-text/60">{formatted || "—"}</span>
                            </div>
                          );
                        } 
                        
                        if (typeof value === "string" && isValidUrl(value)) {
                          return isImageUrl(value) ? (
                            <div className="w-12 h-12 rounded-xl border border-gray-100 overflow-hidden bg-gray-50">
                              <img src={value} alt="image" className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <a href={value} target="_blank" rel="noopener noreferrer" className="text-primary font-black text-[10px] uppercase tracking-widest underline hover:text-[#05322B] transition-colors">
                              Lien
                            </a>
                          );
                        }

                        // Default text style
                        return (
                           <div className="flex flex-col">
                              <span className="text-[13px] font-black text-title uppercase tracking-tight">{value}</span>
                           </div>
                        );
                      })()}
                    </td>
                  );
                })}
                {status && (
                  <td className="px-6 py-5 text-center">
                    {status(row)}
                  </td>
                )}
                {actions && (
                  <td className="px-6 py-5">
                    {actions(row)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="w-20 h-20 rounded-[2rem] bg-gray-50 flex items-center justify-center text-3xl">📭</div>
          <p className="text-[10px] font-black text-text/30 uppercase tracking-[0.4em]">
            Aucune donnée disponible
          </p>
        </div>
      )}
    </div>
  );
};

export default CustomTable;
